import type { SectionView, StepView } from '~/helpers/learner/session-view'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { chapterSlices } from '~/helpers/learner/chapter-slices'
import PathStepItem from '~/pages/scorm/components/PathStepItem'

type IProps = {
  steps: StepView[]
  /** Chapitres du parcours (#75) ; vide ou absent = la liste à plat, comme avant. */
  sections?: readonly SectionView[]
  /** Étape mise en avant dans le panneau principal, pour la souligner ici. */
  currentId?: string | null
  onLaunch: (moduleId: string) => void
}

/**
 * La colonne des étapes d'un parcours (#74), groupée par chapitre quand il en a
 * (#75) — chacun avec son avancement, « 1 / 3 terminés ».
 *
 * Un module verrouillé reste **visible** et porte la raison de sa fermeture —
 * une date à attendre, ou un module à finir (critère de #182). Le masquer
 * laisserait l'apprenant devant un parcours qui rétrécit sans explication.
 *
 * ⚠️ Ces raisons sont LUES, jamais recalculées : `unlocked`, `lockReason` et
 * `unlockAt` viennent de `module-unlock.ts`, seul point d'évaluation du verrou
 * (ADR 0002 § 3). Les chapitres n'y changent rien : ils regroupent l'affichage,
 * ils n'ouvrent ni ne ferment rien.
 */
function PathSteps({ steps, sections = [], currentId, onLaunch }: IProps) {
  const { t } = useTranslation()
  const headingId = useId()

  const list = (slice: StepView[]) => (
    <ol className="flex flex-col gap-1">
      {slice.map(step => (
        <PathStepItem
          key={step.id}
          step={step}
          steps={steps}
          current={step.module?.id != null && step.module.id === currentId}
          onLaunch={onLaunch}
        />
      ))}
    </ol>
  )

  const slices = chapterSlices(steps, sections)
  if (slices.length === 1 && !slices[0]!.section) return list(steps)

  return (
    <div className="flex flex-col gap-4">
      {slices.map(({ section, steps: slice, done }) => {
        // La région porte le même nom que son titre visible : « Chapitre 1 · Les bases ».
        const id = section ? `${headingId}-${section.id}` : undefined
        return (
          <section key={section?.id ?? 'none'} aria-labelledby={id}>
            {section && (
              <div className="mb-1 flex items-baseline justify-between gap-2 px-1">
                <h3 id={id} className="text-sm font-semibold">
                  {t('path.chapter', { number: section.position + 1, title: section.title })}
                </h3>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {t('path.chapter.done', { done, total: slice.length })}
                </span>
              </div>
            )}
            {list(slice)}
          </section>
        )
      })}
    </div>
  )
}

export default PathSteps
